// TB-062: invoice totals. Mirrors transactions-api src/module/invoices/invoice-totals.ts
// so the amount the doctor sees is the amount the backend charges.
// Tax applies to basicFee + extraAmount; every value is rounded to 2 decimals.
import { bhdToUsd, displayCurrency } from '@/utils/displayCurrency'

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100

export function computeInvoiceTotals ({ basicFee, extraAmount, taxRate }) {
  const subtotal = r2((Number(basicFee) || 0) + (Number(extraAmount) || 0))
  const tax = r2((subtotal * (Number(taxRate) || 0)) / 100)
  return { subtotal, tax, total: r2(subtotal + tax) }
}

// TB-110 (Karim 2026-10-21): consultation fees and invoices are BHD. The stored
// number is the BHD amount, shown with 3 decimals and no conversion. No "$"
// anywhere for consultations: a payload without `currency`, or with 'USD',
// still shows totalAmount as BHD.
// en-US grouping on every amount: BHD 4,157.094
const group = (value, digits) => {
  const n = Number(value)
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })
}

export function formatBhd (value) {
  return `BHD ${group(value, 3)}`
}

// TB-122: the admin's display currency. BHD 12.000, or $31.91 at the peg.
// `currency` ('BHD' | 'USD') overrides the setting, e.g. an invoice's displayCurrency.
// TB-123 (W12): USD needs the setting's rate; without it the amount stays BHD.
const pick = (currency) => {
  const c = String(currency || '').toUpperCase()
  const want = c === 'USD' || c === 'BHD' ? c : displayCurrency.currency
  return want === 'USD' && !(displayCurrency.bhdPerUsd > 0) ? 'BHD' : want
}

export function formatDisplay (bhd, currency) {
  if (pick(currency) !== 'USD') return formatBhd(bhd)
  const usd = bhdToUsd(bhd)
  return `${usd < 0 ? '-' : ''}$${group(Math.abs(usd), 2)}`
}

// Display currency of an invoice payload (its displayCurrency), else '' (the setting).
export function invoiceCurrency (inv) {
  const c = inv && inv.displayCurrency
  return c ? String(c).toUpperCase() : ''
}

// One invoice amount (stored BHD) in the display currency. `preformatted` is the
// server's displayAmount(s) string, used as is when present.
export function formatAmount (value, currency, _fx, preformatted) {
  if (typeof preformatted === 'string' && preformatted.trim()) return preformatted
  return formatDisplay(value, currency)
}

// Screen reader phrase: "29.366 Bahraini dinars" / "31.91 US dollars"
export function spokenAmount (value, currency) {
  if (pick(currency) === 'USD') {
    return `${group(bhdToUsd(value), 2)} US dollars`
  }
  return `${group(value, 3)} Bahraini dinars`
}

// The server's display string for one invoice field (basicFee, subtotal, tax, totalAmount), when sent.
export function invoiceDisplayAmount (inv, key = 'totalAmount') {
  if (!inv) return null
  const all = inv.displayAmounts
  if (all && typeof all[key] === 'string') return all[key]
  if (key === 'totalAmount' && typeof inv.displayAmount === 'string') return inv.displayAmount
  return null
}

// API value when present, else the local fallback
export function valueOr (value, fallback) {
  return value === null || value === undefined ? fallback : value
}

export default computeInvoiceTotals
